(() => {
  if (window.collectionQuickViewInitialized) return;
  window.collectionQuickViewInitialized = true;

  const cache = new Map();
  const mobile = window.matchMedia('(max-width: 768px)');
  let dialog;
  let opener;
  let request;
  let generation = 0;
  let product;
  let backdropPointer = false;

  function close(restoreFocus = true) {
    generation += 1;
    if (request) request.abort();
    if (dialog && dialog.open) dialog.close();
    document.documentElement.classList.remove('collection-quick-view-open');
    if (restoreFocus && opener && opener.isConnected) opener.focus({ preventScroll: true });
  }

  function getDialog() {
    const current = document.querySelector('[data-collection-quick-view-modal]');
    if (!current) return null;
    if (current !== dialog) {
      dialog = current;
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        close();
      });
      dialog.addEventListener('close', () => {
        if (!current.open) document.documentElement.classList.remove('collection-quick-view-open');
      });
      dialog.addEventListener('pointerdown', (event) => {
        backdropPointer = event.target === dialog && outsideDialog(event);
      });
      dialog.addEventListener('click', (event) => {
        if (backdropPointer && event.target === dialog && outsideDialog(event)) close();
        backdropPointer = false;
      });
      dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = [...dialog.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled):not([type="hidden"]), select:not(:disabled), summary')]
          .filter((element) => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }
    return dialog;
  }

  function outsideDialog(event) {
    const rect = dialog.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
  }

  async function open(button) {
    if (!getDialog()) return;
    if (request) request.abort();
    const currentGeneration = ++generation;
    opener = button;
    product = null;
    dialog.classList.remove('collection-quick-view--with-image');
    const content = dialog.querySelector('[data-quick-view-content]');
    dialog.removeAttribute('aria-labelledby');
    dialog.setAttribute('aria-label', 'Quick view');
    content.setAttribute('aria-busy', 'true');
    const loading = document.createElement('div');
    loading.className = 'collection-quick-view__loading';
    loading.setAttribute('role', 'status');
    loading.textContent = 'Loading product options…';
    content.replaceChildren(loading);
    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('collection-quick-view-open');
    dialog.querySelector('[data-quick-view-close]').focus({ preventScroll: true });

    const url = new URL(button.dataset.productUrl, window.location.origin);
    const cacheUrl = new URL(url);
    cacheUrl.searchParams.delete('variant');
    cacheUrl.searchParams.delete('view');
    request = new AbortController();
    try {
      let html = cache.get(cacheUrl.href);
      if (!html) {
        const response = await fetch(url.href, { credentials: 'same-origin', signal: request.signal });
        if (!response.ok) throw new Error('Product request failed');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const template = document.querySelector('template[data-collection-quick-view-content]');
        if (!template) throw new Error('Product options are unavailable');
        html = template.innerHTML;
        cache.set(cacheUrl.href, html);
        if (cache.size > 8) cache.delete(cache.keys().next().value);
      }
      if (currentGeneration !== generation || !dialog.open) return;
      const template = document.createElement('template');
      template.innerHTML = html;
      content.replaceChildren(template.content.cloneNode(true));
      product = new QuickViewProduct(content, button.dataset.variantId, url);
      dialog.setAttribute('aria-labelledby', content.querySelector('h2').id);
      dialog.removeAttribute('aria-label');
    } catch (error) {
      if (error.name === 'AbortError' || currentGeneration !== generation) return;
      content.replaceChildren();
      const message = document.createElement('div');
      message.className = 'collection-quick-view__load-error';
      message.setAttribute('role', 'alert');
      message.textContent = 'Unable to load this product. Please try again.';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = 'Try again';
      retry.addEventListener('click', () => open(button));
      const link = document.createElement('a');
      link.href = url.href;
      link.textContent = 'View full details →';
      message.append(document.createElement('br'), retry, link);
      content.append(message);
    } finally {
      if (currentGeneration === generation) content.removeAttribute('aria-busy');
    }
  }

  class QuickViewProduct {
    constructor(container, variantId, url) {
      this.container = container;
      this.data = JSON.parse(container.querySelector('[data-quick-view-data]').textContent);
      this.url = url;
      this.form = container.querySelector('[data-quick-view-form]');
      this.quantity = this.form.elements.quantity;
      this.planSelect = container.querySelector('[data-quick-view-plan]');
      this.submit = container.querySelector('[data-quick-view-submit]');
      this.error = container.querySelector('[data-quick-view-error]');
      this.media = container.querySelector('[data-quick-view-media]');
      this.showImage = dialog.dataset.showImage === 'true';
      this.mode = this.data.subscriptionFirst ? 'subscription' : 'onetime';
      this.variant = this.data.variants.find((item) => String(item.id) === String(variantId))
        || this.data.variants.find((item) => item.available) || this.data.variants[0];
      this.buildOptions();
      this.positionFooter();
      this.positionMedia();
      this.form.addEventListener('change', (event) => {
        if (event.target.name === 'purchase_type') this.mode = event.target.value;
        if (event.target === this.planSelect || event.target.name === 'purchase_type') this.updatePurchase();
        if (event.target === this.quantity) this.updateQuantity();
      });
      this.form.addEventListener('submit', (event) => this.addToCart(event));
      this.updateVariant();
    }

    positionFooter() {
      const footer = this.container.querySelector('.collection-quick-view__footer');
      if (mobile.matches) this.form.append(footer);
      else {
        const supplementary = this.container.querySelector('[data-quick-view-nutrition]');
        supplementary.before(footer);
      }
    }

    positionMedia() {
      if (mobile.matches) this.container.querySelector('.collection-quick-view__header').prepend(this.media);
      else this.container.querySelector('[data-quick-view-product]').prepend(this.media);
    }

    updateImage() {
      const image = this.showImage && ((this.variant && this.variant.image) || this.data.featuredImage);
      this.media.hidden = !image;
      dialog.classList.toggle('collection-quick-view--with-image', !!image);
      if (!image) return;
      let element = this.media.querySelector('img');
      if (!element) {
        element = document.createElement('img');
        element.decoding = 'async';
        this.media.append(element);
      }
      element.alt = image.alt;
      element.width = image.width;
      element.height = image.height;
      if (element.getAttribute('src') !== image.src) element.src = image.src;
    }

    money(cents) {
      const theme = window.theme;
      if (theme && theme.Currency) {
        const formatted = theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
        return new DOMParser().parseFromString(formatted, 'text/html').body.textContent;
      }
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.data.currency || 'USD',
      }).format(cents / 100);
    }

    buildOptions() {
      this.selects = [];
      this.swatches = [];
      if (this.data.hasOnlyDefaultVariant) return;
      const wrapper = this.container.querySelector('[data-quick-view-options]');
      this.data.options.forEach((name, index) => {
        const field = document.createElement('div');
        field.className = 'collection-quick-view__option';
        const label = document.createElement('label');
        label.className = 'text-label';
        const select = document.createElement('select');
        select.id = `QuickViewOption-${this.data.id}-${index}`;
        label.htmlFor = select.id;
        label.textContent = `Choose a ${name}`;
        const values = [...new Set(this.data.variants.map((variant) => variant.options[index]))];
        values.forEach((value) => select.add(new Option(value, value)));
        select.addEventListener('change', () => {
          const values = this.selects.map((element) => element.value);
          const exact = this.data.variants.find((variant) => variant.options.every((value, position) => value === values[position]));
          const candidates = this.data.variants.filter((variant) => variant.options.every((value, position) => position > index || value === values[position]));
          this.variant = exact || candidates.find((variant) => variant.available) || candidates[0];
          this.updateVariant();
        });
        this.selects.push(select);
        const swatchTemplate = this.container.querySelector(`template[data-quick-view-swatches="${index}"]`);
        if (swatchTemplate) {
          select.hidden = true;
          field.append(select, swatchTemplate.content.cloneNode(true));
          const list = field.querySelector('.variant-input-wrap');
          const button = field.querySelector('[data-flavor-toggle]');
          const heading = field.querySelector('.variant__label');
          const selectedValue = document.createElement('strong');
          selectedValue.className = 'collection-quick-view__selected-flavor';
          if (heading.lastChild && heading.lastChild.nodeType === Node.TEXT_NODE) {
            heading.lastChild.textContent = heading.lastChild.textContent.trimEnd();
          }
          heading.append(': ', selectedValue);
          const radios = [...list.querySelectorAll('input[type="radio"]')].filter((radio) => {
            if (values.includes(radio.value)) return true;
            radio.closest('.variant-input').remove();
            return false;
          });
          radios.forEach((radio) => {
            radio.name = `quick_view_option_${index}`;
            radio.addEventListener('change', () => {
              select.value = radio.value;
              select.dispatchEvent(new Event('change', { bubbles: true }));
            });
          });
          const swatch = { index, list, button, radios, selectedValue, expanded: false };
          button.addEventListener('click', () => {
            swatch.expanded = !swatch.expanded;
            this.updateSwatchRows(swatch);
          });
          this.swatches.push(swatch);
        } else {
          field.append(label, select);
        }
        wrapper.append(field);
      });
    }

    updateSwatches() {
      this.swatches.forEach((swatch) => {
        const select = this.selects[swatch.index];
        swatch.selectedValue.textContent = select.value;
        swatch.radios.forEach((radio) => {
          const option = [...select.options].find((item) => item.value === radio.value);
          radio.checked = select.value === radio.value;
          radio.disabled = !option || option.disabled;
          const available = this.data.variants.some((variant) => variant.available
            && variant.options[swatch.index] === radio.value
            && variant.options.slice(0, swatch.index).every((value, position) => value === this.selects[position].value));
          radio.classList.toggle('disabled', !available);
          radio.nextElementSibling.classList.toggle('disabled', !available);
        });
        this.updateSwatchRows(swatch, true);
      });
    }

    updateSwatchRows(swatch, revealSelected = false) {
      const items = swatch.radios.map((radio) => radio.closest('.variant-input'));
      items.forEach((item) => { item.hidden = false; });
      // Hiding items changes the grid layout, so measure every position first.
      const positions = items.map((item) => item.offsetTop);
      const rows = [...new Set(positions)].sort((a, b) => a - b);
      const hasExtraRows = rows.length > 2;
      if (hasExtraRows) {
        const cutoff = rows[2] - 2;
        const selectedIndex = swatch.radios.findIndex((radio) => radio.checked);
        if (revealSelected && selectedIndex !== -1 && positions[selectedIndex] >= cutoff) swatch.expanded = true;
        if (!swatch.expanded) items.forEach((item, index) => { item.hidden = positions[index] >= cutoff; });
      }
      swatch.button.hidden = !hasExtraRows;
      swatch.button.textContent = swatch.expanded ? 'See Less' : 'See More';
      swatch.button.setAttribute('aria-expanded', String(swatch.expanded));
    }

    updateVariant() {
      this.error.hidden = true;
      if (this.variant) {
        this.selects.forEach((select, index) => {
          select.value = this.variant.options[index];
          [...select.options].forEach((option) => {
            option.disabled = !this.data.variants.some((variant) => variant.options[index] === option.value
              && variant.options.slice(0, index).every((value, position) => value === this.variant.options[position]));
          });
        });
        this.form.elements.id.value = this.variant.id;
        this.url.searchParams.set('variant', this.variant.id);
      }
      this.container.querySelector('[data-quick-view-details]').href = this.url.href;
      const previousPlan = this.planSelect.value;
      this.planSelect.replaceChildren();
      const plans = this.variant && !this.variant.oneTimeOnly ? this.variant.plans : [];
      plans.forEach((plan) => this.planSelect.add(new Option(plan.name, plan.id)));
      const selected = plans.find((plan) => String(plan.id) === previousPlan)
        || plans.find((plan) => plan.name === 'Delivery every 4 weeks') || plans[0];
      if (selected) this.planSelect.value = selected.id;
      this.updatePurchase();
      this.updateNutrition();
      this.updateImage();
      this.updateSwatches();
    }

    updateNutrition() {
      const nutrition = this.container.querySelector('[data-quick-view-nutrition]');
      if (!nutrition) return;
      const panels = [...nutrition.querySelectorAll('[data-quick-view-nutrition-variant]')];
      const selected = this.variant && panels.find((panel) => panel.dataset.quickViewNutritionVariant === String(this.variant.id));
      const fallback = nutrition.querySelector('[data-quick-view-nutrition-fallback]');
      const active = selected || (this.variant && fallback);
      panels.forEach((panel) => { panel.hidden = panel !== active; });
      if (fallback) fallback.hidden = fallback !== active;
      nutrition.hidden = !active;
      if (!active) nutrition.open = false;
    }

    updatePurchase() {
      const variant = this.variant;
      const plans = variant && !variant.oneTimeOnly ? variant.plans : [];
      const plan = plans.find((item) => String(item.id) === this.planSelect.value);
      const subscription = !!plan && (this.mode === 'subscription' || this.data.subscriptionOnly);
      this.activePlan = subscription ? plan : null;
      this.container.querySelector('[data-quick-view-subscription]').hidden = !plans.length || this.data.configureOnPdp;
      this.container.querySelector('[data-quick-view-onetime]').hidden = this.data.subscriptionOnly || this.data.configureOnPdp;
      this.planSelect.disabled = !subscription;
      const terms = this.container.querySelector('[data-quick-view-subscription-terms]');
      terms.hidden = !subscription || this.data.configureOnPdp;
      terms.setAttribute('aria-hidden', String(terms.hidden));
      this.form.querySelectorAll('[name="purchase_type"]').forEach((radio) => {
        radio.checked = radio.value === (subscription ? 'subscription' : 'onetime');
        radio.disabled = radio.value === 'subscription' ? !plans.length : this.data.subscriptionOnly;
      });
      const planInput = this.form.elements.selling_plan;
      planInput.disabled = !subscription;
      planInput.value = subscription ? plan.id : '';
      const price = variant ? variant.price : 0;
      this.container.querySelector('[data-quick-view-onetime-price]').textContent = this.money(price);
      if (plan) {
        const saving = Math.max(0, price - plan.price);
        this.container.querySelector('[data-quick-view-subscription-label]').textContent =
          `Subscribe${saving ? ` (${this.money(saving)} OFF)` : ''}: ${this.money(plan.price)}`;
      }
      this.updateQuantity();
      const available = !!variant && variant.available && (!this.data.subscriptionOnly || subscription);
      this.submit.disabled = !available;
      this.submit.textContent = !variant ? this.data.unavailableText
        : !available ? this.data.soldOutText : subscription ? this.data.subscribeText : this.data.addText;
      if (this.data.configureOnPdp) {
        this.submit.disabled = false;
        this.submit.textContent = 'View product options';
      }
      this.container.querySelector('[data-quick-view-quantity]').hidden = this.data.configureOnPdp;
      const serving = this.container.querySelector('[data-serving-details]');
      if (serving) {
        let visible = false;
        serving.querySelectorAll('[data-serving-variant]').forEach((group) => {
          group.hidden = !variant || group.dataset.servingVariant !== String(variant.id);
          if (group.hidden) return;
          visible = true;
          const amount = group.querySelector('[data-serving-price]');
          const total = Number(group.dataset.totalServings);
          if (amount && total > 0) amount.textContent = this.money(Math.round((subscription ? plan.price : price) / total));
        });
        serving.hidden = !visible;
      }
    }

    updateQuantity() {
      const rule = (this.variant && this.variant.quantityRule) || {};
      const fixed = this.activePlan && this.activePlan.fixedQuantity ? Number(this.activePlan.quantity) : 0;
      const minimum = fixed || Math.max(1, Number(rule.min) || 1);
      const step = Math.max(1, Number(rule.increment) || 1);
      const maximum = fixed || Number(rule.max) || Number(this.data.promoLimit) || Infinity;
      let quantity = Number(this.quantity.value);
      if (!Number.isFinite(quantity)) quantity = minimum;
      quantity = minimum + Math.max(0, Math.round((quantity - minimum) / step)) * step;
      this.quantity.min = minimum;
      this.quantity.step = step;
      if (Number.isFinite(maximum)) this.quantity.max = maximum;
      else this.quantity.removeAttribute('max');
      this.quantity.value = Math.min(maximum, quantity);
      this.quantity.readOnly = !!fixed;
      this.form.querySelector('[data-quick-view-adjust="-1"]').disabled = !!fixed || Number(this.quantity.value) <= minimum;
      this.form.querySelector('[data-quick-view-adjust="1"]').disabled = !!fixed || Number(this.quantity.value) >= maximum;
    }

    async addToCart(event) {
      event.preventDefault();
      if (this.data.configureOnPdp) {
        window.location.assign(this.url.href);
        return;
      }
      if (this.saving || this.submit.disabled) return;
      this.updateQuantity();
      if (!this.form.reportValidity()) return;
      const payload = new FormData(this.form);
      payload.delete('purchase_type');
      this.swatches.forEach((swatch) => payload.delete(`quick_view_option_${swatch.index}`));
      this.saving = true;
      this.form.inert = true;
      this.error.hidden = true;
      this.submit.disabled = true;
      this.submit.setAttribute('aria-busy', 'true');
      this.submit.classList.add('btn--loading');
      try {
        const theme = window.theme;
        if (this.data.promoLimit > 0) {
          const response = await fetch(theme.routes.cart, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Unable to check cart quantity. Please try again.');
          const cart = await response.json();
          const existing = cart.items.filter((item) => String(item.product_id) === String(this.data.id)).reduce((total, item) => total + item.quantity, 0);
          if (existing + Number(payload.get('quantity')) > this.data.promoLimit) {
            throw new Error(`You can add up to ${this.data.promoLimit} of this product. Please adjust your cart quantity.`);
          }
        }
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST', credentials: 'same-origin', body: payload,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        const result = await response.json();
        if (!response.ok || result.status >= 400) throw new Error(result.description || 'Unable to add this product. Please try again.');
        if (theme.cart && theme.cart.invalidateCart) theme.cart.invalidateCart();
        close(false);
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: result, addToCartBtn: opener } }));
        if (theme.settings.cartType !== 'drawer') window.location.assign(theme.routes.cartPage);
      } catch (error) {
        this.error.textContent = error instanceof TypeError || error instanceof SyntaxError
          ? 'Unable to add this product. Please check your connection and try again.'
          : error.message || 'Unable to add this product. Please try again.';
        this.error.hidden = false;
        this.error.scrollIntoView({ block: 'nearest' });
      } finally {
        this.saving = false;
        this.form.inert = false;
        this.submit.removeAttribute('aria-busy');
        this.submit.classList.remove('btn--loading');
        this.updatePurchase();
      }
    }
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-collection-quick-view]');
    if (trigger) {
      event.preventDefault();
      open(trigger);
      return;
    }
    if (event.target.closest('[data-quick-view-close]')) close();
    const adjust = event.target.closest('[data-quick-view-adjust]');
    if (adjust && product && !adjust.disabled) {
      product.quantity.value = Number(product.quantity.value) + Number(adjust.dataset.quickViewAdjust) * Number(product.quantity.step);
      product.updateQuantity();
    }
  });
  document.addEventListener('shopify:section:unload', (event) => {
    if (dialog && event.target.contains(dialog)) close();
  });
  mobile.addEventListener('change', () => {
    if (product) {
      product.positionFooter();
      product.positionMedia();
    }
  });
  let swatchResizeTimer;
  window.addEventListener('resize', () => {
    window.clearTimeout(swatchResizeTimer);
    swatchResizeTimer = window.setTimeout(() => {
      if (product && dialog && dialog.open) product.updateSwatches();
    }, 150);
  });
})();
